
import {
    Component,
    forwardRef,
    KeyValueDiffers,
    Inject,
    OnChanges,
    ViewChild,
    OnInit,
    ElementRef,
    ChangeDetectorRef,
    HostListener,
    OnDestroy
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import * as htmlToImage from 'html-to-image';
import * as am4core from '@amcharts/amcharts4/core';

import { environment } from '@env/environment';
import { download } from '../../../Misc/helpers/download/download.helper';
import { BlockComponent } from '../../../PageBuilder/components/Block/block.component';
import { SeriesModel } from '../../../Charts/models/series.model';
import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { ReportsPageBuilderService } from '../../services/reports-page-builder.service';
import { ChartBlockComponent } from '../ChartBlock/chart-block.component';
import { ReportsSeriesPreviewService } from '../../services/report-series-preview.service';
import { ReportsChartOptionsService } from '../../services/reports-chart-options.service';
import { chartColors } from '../../../Charts/enums/chart-colors.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { ClickOutsideService } from '../../../PageBuilder/services/click-outside.service';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { ReportsChartDetailService } from '../../services/reports-chart-detail.service';
import { printHelper } from '../../../Misc/helpers/print/print.helper';
import { ChartExportEventModel } from '../../../Charts/models/chart-export-event.model';
import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { WordCloudChartOptionsModel } from '../../../Charts/models/WordCloudChart/word-cloud-chart-options.model';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { ChartSizeHelper } from '../../../Charts/helpers/chart-size.helper';
import { SearchCriteriaSortFieldEnum } from '../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { TopPostsChartComponent } from '../TopPostsChart/top-posts-chart.component';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { ReportsChartSeriesConverterService } from '../../services/reports-chart-series-converter.service';
import { ReportsSeriesPreviewNewService } from '../../../../Common/Reports/services/reports-series-preview-new.service';


@Component({
    selector: 'agpr-top-post-chart-block',
    templateUrl: './top-post-chart-block.component.html',
    styleUrls: ['./top-post-chart-block.component.scss'],
    providers: [
        {
            provide: BlockComponent,
            useExisting: forwardRef(() => TopPostChartBlockComponent)
        }
    ],
    standalone: false
})
export class TopPostChartBlockComponent extends ChartBlockComponent implements OnChanges, OnInit, OnDestroy {
    readonly searchCriteriaSortFieldEnum = SearchCriteriaSortFieldEnum;
    protected exportClass: string = 'agpr-topPostChart--exporting';
    public series: SeriesModel[];
    public sourceRouterLink: string = '';
    public sourceRouterLinkQueryParams: any = {};
    public sourceId: string = '';
    public color: string = chartColors.agprBlue;
    noHeader: boolean;
    small: boolean = false;
    declare chartOptions: WordCloudChartOptionsModel;
    isExporting: boolean = false;

    protected userPreference: PreferencesModel;

    chartResizeObserver = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        const height = entries[0].contentRect.height;
        this.small = width <= 300 || height <= ChartSizeHelper.getHeight(3, 1);
        this.changeDetectorRef.detectChanges();
    });

    @ViewChild('exportMenu') exportMenu;
    @ViewChild('topPostChart') topPostChart: TopPostsChartComponent;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public elementRef: ElementRef,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        protected userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService
    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            reportsChartOptionsService,
            localizationService,
            appBaseHref,
            changeDetectorRef,
            userStateService,
            null,
            monitorConfigService,
            userPreferencesService,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            elementRef,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initSourceRouterLink();
        this.chartResizeObserver.observe(this.elementRef.nativeElement?.children?.[0]);
    }

    ngOnDestroy(): void {
        this.chartResizeObserver.unobserve(this.elementRef.nativeElement?.children?.[0]);
        this.chartResizeObserver.disconnect();
        super.ngOnDestroy();
    }

    ngOnChanges(): void {
        this.updateChart();
        this.noHeader = this.displayElement.chartHeaderSettings.hasNoElements();
    }

    /**
     * Checks every <img> under the given element for whether it can actually be fetched
     * (not just displayed). If not, tries fetching it through our proxy instead.
     * Returns the images that still can't be fetched, so we can exclude them
     * from an html-to-image export instead of letting a blocked fetch fail the whole export.
     */
    private async getUnloadableImages(container: HTMLElement): Promise<Set<HTMLElement>> {
        const images = Array.from(container.querySelectorAll('img'));
        const unloadable = new Set<HTMLElement>();

        await Promise.all(images.map(async img => {
            try {
                const response = await fetch(img.src);
                if (!response.ok) {
                    throw new Error(`Failed to fetch image: ${response.status}`);
                }
            } catch {
                try {
                    const proxyUrl = environment.apiGatewayUrl + '/proxy?responseType=blob&url=' + encodeURIComponent(img.src);
                    const response = await fetch(proxyUrl);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch proxied image: ${response.status}`);
                    }
                    img.src = proxyUrl;
                } catch {
                    unloadable.add(img);
                }
            }
        }));

        return unloadable;
    }

    updateChart() {
        this.displayElement = this.block.displayElement as ChartDisplayElementModel;
        if (!this.displayElement || !this.displayElement.series || this.displayElement.series.length < 1) {
            this.series = null;
            return;
        }

        this.renderable = this.block.displayElement.canRender();
        this.color = this.displayElement.styles.colorMap.barColor;

        const series = this.displayElement.series.filter(seriesItem =>
            seriesItem.isValid() || this.displayElement.isCustomSortFieldValid(seriesItem.chartType)
        );
        if (series.length < 1) {
            this.series = null;
            return;
        }
        series.forEach(_series => {
            _series.styles.networkSelection = this.displayElement.networkSelection;
        });

        if (this.preview) {
            this.series = this.reportSeriesPreviewService.getPreviewSeries(
                this.block.type.key as ReportBlockTypeEnum,
                series,
                this.block.displayElement as ChartDisplayElementModel);
        } else {
            this.series = series;
        }

        this.setLabels();
        if ((this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent) {
            (this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent(this.block, this);
        }
    }

    protected setLabels(): void  {
        if (this.displayElement?.series?.[0]?.dateRange
            // For shared report, we already set its relative and absolute date rage when mapping api blocks
            // For date range "ALL" scenario, we cannot do localization in model, so we keep it here
            && (!this.displayElement.series[0].isSharedReport || this.displayElement?.series?.[0]?.dateRange.type === DateQueryTypeEnum.ALL)
        ) {
            this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series);
        }
    }

    /**
     * Handles the export button click action, exporting the chart in the selected format
     */
    public async chartExport(event: ChartExportEventModel) {
        this.isExporting = true;
        // this removes the :hover rule that causes the border of the chart to be blue
        const container: HTMLDivElement = this.elementRef.nativeElement.children[0];

        const containerToExport = container.getElementsByClassName('agpr-chartBlock-content')?.[0] as HTMLDivElement;
        const metaDataHeight = container.getElementsByClassName('agpr-chartBlockMetadata')?.[0]?.clientHeight || 0;
        const post = container.getElementsByClassName('agpr-topPostChart-container')?.[0];
        const newHeight = post.getElementsByClassName('p-scrollpanel-content')?.[0].scrollHeight || 0;

        containerToExport.style.height = (metaDataHeight + newHeight) + 'px';

        this.exportMenu.nativeElement.style.display = 'none';

        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;

        let url: string;
        let blob: Blob;

        const tempData = this.topPostChart.data;
        // There's no easy way to extend the length of the chart image to show all the data.
        // Slice the data to 5 before gererating the image because it gets cut off when data length is more than 5.
        // when image is generated, put the original data back otherwise it will only show 5 .
        this.topPostChart.data = this.topPostChart.data.slice(0, 5);
        requestAnimationFrame(async () => {
            switch (exportType) {
                case 'jpg':
                    url = await htmlToImage.toJpeg(containerToExport, { backgroundColor: 'white' });
                    break;

                case 'png':
                    url = await htmlToImage.toPng(containerToExport, { backgroundColor: 'white' });
                    break;
                case 'svg':
                    url = await htmlToImage.toSvg(containerToExport);
                    break;

                case 'print':
                    const printData = await htmlToImage.toSvg(containerToExport);
                    printHelper.image(printData, document.title, {fit: containerToExport.offsetHeight <= 1500});
                    break;
            }

            if (blob) {
                download.blob(blob, `${this.displayElement.title}.${exportType}`);

            }

            if (url) {
                download.url(url, `${this.displayElement.title}.${exportType}`);
            }

            this.exportMenu.nativeElement.style.display = 'block';
            this.topPostChart.data = tempData;
            containerToExport.style.height = null;
            this.isExporting = false;
        });

    }

    /**
    * Gets the export image for the full report pdf export
    */
    public async getFullExportImage(width: number, height: number): Promise<string> {
        const el: HTMLDivElement = this.elementRef.nativeElement.children[0];
        const classes: DOMTokenList = this.elementRef.nativeElement.children[0].classList;

        const metaDataHeight = el.getElementsByClassName('agpr-chartBlockMetadata')?.[0]?.clientHeight || 0;
        const post = el.getElementsByClassName('agpr-topPostChart-container')?.[0];
        const newHeight = post.getElementsByClassName('p-scrollpanel-content')?.[0].scrollHeight || 0;

        const metaDataWdith = el.getElementsByClassName('agpr-chartBlockMetadata')?.[0]?.clientWidth || 0;

        el.style.border = 'none';

        // Sets a bunch of styles for scaling to the page size of the pdf
        classes.add(this.exportClass);
        el.style.width = (metaDataWdith + 40) + 'px';
        el.style.minWidth = (metaDataWdith + 40) + 'px';
        el.style.height = (metaDataHeight + newHeight) + 'px';
        el.style.minHeight = (metaDataHeight + newHeight) + 'px';
        el.style.flex = 'unset';

        // There's no easy way to extend the length of the chart image to show all the data.
        // Slice the data to 5 before gererating the image because it gets cut off when data length is more than 5.
        // when image is generated, put the original data back otherwise it will only show 5 .
        const tempData = this.topPostChart.data;
        this.topPostChart.data = this.topPostChart.data.slice(0, 5);

        // Post thumbnails can be hosted on CDNs that don't send CORS headers. html-to-image needs to
        // fetch each image to inline it, and one blocked fetch rejects the whole export, so pre-check
        // and drop only the images that actually fail to fetch.
        const unloadableImages = await this.getUnloadableImages(el);

        let png;
        try {
            png = await htmlToImage.toPng(this.elementRef.nativeElement.children[0], {
                filter: node => !unloadableImages.has(node)
            });
        } catch (e) {}

        // Resets the styles to the original
        el.style.width = '';
        el.style.minWidth = '';
        el.style.height = '';
        el.style.minHeight = '';
        el.style.flex = '';

        el.style.border = `1px solid ${chartColors.agprLightGrey}`;
        this.topPostChart.data = tempData;
        classes.remove(this.exportClass);

        return png;
    }

    /**
     * Constructs the url to the topic or folder currently being used as a source for this chart.
     * Tags & Tag Groups are also handled, by constructing a url to all saved coverage with appropriate filters
     * applied via queryParams
     */
    public initSourceRouterLink(): void {
        const displayElement = this.block.displayElement as ChartDisplayElementModel;

        if (displayElement?.series?.[0]) {
            const urlParts = displayElement.series[0].getMentionSeriesUrl().split('?filters=');
            this.sourceRouterLink = '/' + urlParts?.[0];
            this.sourceRouterLinkQueryParams = { filters: urlParts?.[1] };
        }
    }

    formatDateRangeLabel(format: string) {
        this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series, null, format);
        this.changeDetectorRef.detectChanges();
    }
}
